<template>
  <div
    class="main-menu menu-fixed menu-accordion menu-shadow"
    :class="[
      { 'expanded': !isVerticalMenuCollapsed || (isVerticalMenuCollapsed && isMouseHovered) },
      { 'sidebar-search-active': isSidebarSearchFocused || menuSearch.trim() },
      skin === 'semi-dark' ? 'menu-dark' : 'menu-light'
    ]"
    @mouseenter="handleMouseHovered(true)"
    @mouseleave="handleMouseHovered(false)"
  >
    <!-- main menu header-->
    <div class="navbar-header expanded">
      <slot
        name="header"
        :toggleVerticalMenuActive="toggleVerticalMenuActive"
        :toggleCollapsed="toggleCollapsed"
        :collapseTogglerIcon="collapseTogglerIcon"
      >
        <ul class="nav navbar-nav flex-row">

          <!-- Logo & Text -->
          <li class="nav-item mr-auto">
            <b-link
              class="navbar-brand"
              to="/"
            >
              <span class="brand-logo">
                <b-img
                  :src="appLogoImage"
                  alt="logo"
                />
              </span>
              <h2 class="brand-text">
                {{ appName }}
              </h2>
            </b-link>
          </li>

          <!-- Toggler Button (mobile close only) -->
          <li class="nav-item nav-toggle">
            <b-link
              class="nav-link modern-nav-toggle sidebar-collapse-btn d-block d-xl-none"
              title="Close sidebar"
            >
              <feather-icon
                icon="XIcon"
                size="20"
                @click="toggleVerticalMenuActive"
              />
            </b-link>
          </li>
        </ul>
      </slot>
    </div>
    <!-- / main menu header-->

    <!-- Edge collapse toggle (desktop only) -->
    <button
      type="button"
      class="sidebar-edge-toggle d-none d-xl-flex"
      :title="isVerticalMenuCollapsed ? 'Expand sidebar' : 'Collapse sidebar'"
      @click="toggleCollapsed"
    >
      <feather-icon
        :icon="sidebarToggleIcon"
        size="14"
        class="collapse-toggle-icon"
      />
    </button>

    <!-- Shadow -->
    <div
      :class="{'d-block': shallShadowBottom}"
      class="shadow-bottom"
    />

    <div class="sidebar-search">
      <b-input-group
        class="input-group-merge"
        @click.stop
        @mousedown.stop
      >
        <b-input-group-prepend is-text>
          <feather-icon
            icon="SearchIcon"
            size="16"
          />
        </b-input-group-prepend>
        <b-form-input
          ref="sidebarSearchInput"
          v-model="menuSearch"
          autocomplete="off"
          placeholder="Search menu"
          :readonly="false"
          @focus="unlockSidebarSearch"
          @blur="handleSidebarSearchBlur"
          @mousedown.stop="unlockSidebarSearch"
          @click.stop="unlockSidebarSearch"
          @keydown.stop
        />
      </b-input-group>
    </div>

    <!-- main menu content-->
    <vue-perfect-scrollbar
      :settings="perfectScrollbarSettings"
      class="main-menu-content scroll-area"
      tagname="ul"
      @ps-scroll-y="evt => { shallShadowBottom = evt.srcElement.scrollTop > 0 }"
    >
      <vertical-nav-menu-items
        :items="filteredNavMenuItems"
        class="navigation navigation-main"
        @click.native="handleMenuClick"
      />
      <li
        v-if="hasSearch && !filteredNavMenuItems.length"
        class="sidebar-empty-state"
      >
        <feather-icon
          icon="SearchIcon"
          size="18"
        />
        <span>No menu found</span>
      </li>
    </vue-perfect-scrollbar>
    <!-- /main menu content-->

    <div class="sidebar-account-panel">
      <b-link
        class="sidebar-account-card"
        :to="{ name: 'apps-users-profile' }"
      >
        <b-avatar
          size="36"
          variant="light-primary"
          :text="userInitials"
          class="sidebar-account-avatar"
        />
        <span class="sidebar-account-card__copy">
          <strong>{{ displayName }}</strong>
          <small>{{ roleName }}</small>
        </span>
      </b-link>
      <div class="sidebar-account-actions">
        <b-link
          class="sidebar-account-action"
          :to="{ name: 'apps-users-profile' }"
        >
          <feather-icon
            icon="UserIcon"
            size="15"
          />
          <span>Profile</span>
        </b-link>
        <button
          type="button"
          class="sidebar-account-action sidebar-account-action--danger"
          @click="logout"
        >
          <feather-icon
            icon="LogOutIcon"
            size="15"
          />
          <span>Sign out</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue'
import router from '@/router'
import navMenuItems from '@/navigation/vertical'
import VuePerfectScrollbar from 'vue-perfect-scrollbar'
import {
  BLink, BImg, BInputGroup, BInputGroupPrepend, BFormInput, BAvatar,
} from 'bootstrap-vue'
import {
  provide, computed, ref, onMounted, onUnmounted, nextTick, watch,
} from '@vue/composition-api'
import useAppConfig from '@core/app-config/useAppConfig'
import { $themeConfig } from '@themeConfig'
import VerticalNavMenuItems from './components/vertical-nav-menu-items/VerticalNavMenuItems.vue'
import useVerticalNavMenu from './useVerticalNavMenu'

export default {
  components: {
    VuePerfectScrollbar,
    VerticalNavMenuItems,
    BLink,
    BImg,
    BInputGroup,
    BInputGroupPrepend,
    BFormInput,
    BAvatar,
  },
  props: {
    isVerticalMenuActive: {
      type: Boolean,
      required: true,
    },
    toggleVerticalMenuActive: {
      type: Function,
      required: true,
    },
  },
  setup(props) {
    const {
      isMouseHovered,
      isVerticalMenuCollapsed,
      collapseTogglerIcon,
      toggleCollapsed,
      updateMouseHovered,
    } = useVerticalNavMenu(props)

    const { skin } = useAppConfig()

    // Shadow bottom is UI specific and can be removed by user => It's not in `useVerticalNavMenu`
    const shallShadowBottom = ref(false)
    // `menuSearch` is bound directly to the input for instant typing feedback.
    // `menuSearchQuery` is a debounced copy used to drive the (heavier) list
    // filtering, so re-rendering the whole menu tree on every keystroke can't
    // interfere with the input's own focus/DOM state.
    const menuSearch = ref('')
    const menuSearchQuery = ref('')
    const isSidebarSearchFocused = ref(false)
    let searchDebounceTimer = null
    watch(menuSearch, value => {
      if (searchDebounceTimer) clearTimeout(searchDebounceTimer)
      searchDebounceTimer = setTimeout(() => {
        menuSearchQuery.value = value
      }, 180)
    })
    onUnmounted(() => {
      if (searchDebounceTimer) clearTimeout(searchDebounceTimer)
    })
    const sidebarSearchInput = ref(null)
    const userData = ref(Vue.$cookies.get('userData') || {})

    provide('isMouseHovered', isMouseHovered)

    const perfectScrollbarSettings = {
      maxScrollbarLength: 60,
      wheelPropagation: false,
    }

    const sidebarToggleIcon = computed(() => (isVerticalMenuCollapsed.value ? 'ChevronRightIcon' : 'ChevronLeftIcon'))

    const displayName = computed(() => userData.value?.user_fullname || userData.value?.FullName || 'GeoProc User')

    const roleName = computed(() => (
      userData.value?.role?.role_name
      || userData.value?.UserRoleName
      || userData.value?.role_name
      || 'User'
    ))

    const userInitials = computed(() => displayName.value
      .split(' ')
      .filter(Boolean)
      .slice(0, 2)
      .map(part => part.charAt(0))
      .join('')
      .toUpperCase())

    const normalize = value => String(value || '').toLowerCase()

    const getSearchText = (item, parentTitle = '') => [
      item.title,
      item.header,
      item.route,
      item.icon,
      parentTitle,
    ].map(normalize).join(' ')

    const flattenMatchingItems = (items, query, parentTitle = '') => items.reduce((result, item) => {
      if (item.children) {
        const groupMatches = getSearchText(item, parentTitle).includes(query)
        const matchedChildren = groupMatches
          ? item.children
          : flattenMatchingItems(item.children, query, item.title)

        return [...result, ...matchedChildren]
      }

      if (getSearchText(item, parentTitle).includes(query)) {
        return [...result, item]
      }

      return result
    }, [])

    const hasSearch = computed(() => menuSearchQuery.value.trim().length > 0)

    const filteredNavMenuItems = computed(() => {
      const query = normalize(menuSearchQuery.value.trim())

      if (!query) return navMenuItems

      return flattenMatchingItems(navMenuItems, query)
    })

    const handleMenuClick = evt => {
      const { target } = evt
      const link = target && target.closest ? target.closest('a') : null
      if (!link) return

      // Ignore clicks on group togglers (submenu expand/collapse) — these don't
      // navigate anywhere, so the mobile sidebar should stay open. Only close it
      // when an actual leaf link (a real navigable route) is tapped.
      const navItem = link.closest ? link.closest('.nav-item') : null
      if (navItem && navItem.classList.contains('has-sub')) return

      if (hasSearch.value) {
        menuSearch.value = ''
        menuSearchQuery.value = ''
      }
      if (window.innerWidth < 1200) props.toggleVerticalMenuActive()
    }

    const getSidebarSearchInput = () => {
      if (!sidebarSearchInput.value) return null
      if (sidebarSearchInput.value.$el) {
        return sidebarSearchInput.value.$el.matches('input')
          ? sidebarSearchInput.value.$el
          : sidebarSearchInput.value.$el.querySelector('input')
      }
      return sidebarSearchInput.value
    }

    const focusSidebarSearch = () => {
      const input = getSidebarSearchInput()
      if (!input) return

      input.focus()
      const cursorPosition = input.value.length
      if (input.setSelectionRange) input.setSelectionRange(cursorPosition, cursorPosition)
    }

    watch(menuSearchQuery, () => {
      if (isSidebarSearchFocused.value) {
        nextTick(() => {
          focusSidebarSearch()
        })
      }
    })

    // Just strips the readonly/disabled attributes off the search input -
    // safe to call any time, including on mount, since it doesn't touch
    // hover/focus state.
    const unlockSidebarSearchInput = () => {
      const input = getSidebarSearchInput()
      if (input) {
        input.removeAttribute('readonly')
        input.removeAttribute('disabled')
      }
    }

    // Real user interaction with the search box: also marks the sidebar as
    // "focused"/hovered so it stays expanded while searching. Must only run
    // from genuine focus/mousedown/click events (see the search <input>
    // below) - NOT on mount. Calling this on mount (as a previous version
    // did via onMounted) sets isSidebarSearchFocused=true without the input
    // ever receiving real DOM focus, so no blur event ever fires to reset
    // it. handleMouseHovered's mouseleave guard then sees
    // isSidebarSearchFocused stuck true forever and refuses to un-hover,
    // permanently pinning the sidebar in its expanded ("hovered") width
    // even while collapsed - the collapse toggle updates Vuex/content layout
    // correctly, but the sidebar itself never visually shrinks.
    const unlockSidebarSearch = () => {
      isSidebarSearchFocused.value = true
      updateMouseHovered(true)
      unlockSidebarSearchInput()
    }

    const handleSidebarSearchBlur = () => {
      window.setTimeout(() => {
        const input = getSidebarSearchInput()
        const stillFocused = input && document.activeElement === input

        isSidebarSearchFocused.value = stillFocused
        if (stillFocused || menuSearch.value.trim()) updateMouseHovered(true)
      }, 0)
    }

    const handleMouseHovered = value => {
      if (!value && (isSidebarSearchFocused.value || menuSearch.value.trim())) {
        updateMouseHovered(true)
        return
      }

      updateMouseHovered(value)
    }

    onMounted(() => {
      nextTick(unlockSidebarSearchInput)
    })

    const logout = () => {
      Vue.$cookies.remove('userToken')
      Vue.$cookies.remove('userData')
      Vue.$cookies.remove('userPermissions')
      Vue.$cookies.remove('UserRole')
      localStorage.removeItem('userData')
      localStorage.removeItem('accessToken')
      router.push({ name: 'auth-login' })
    }

    // App Name
    const { appName, appLogoImage } = $themeConfig.app

    return {
      navMenuItems,
      filteredNavMenuItems,
      perfectScrollbarSettings,
      isVerticalMenuCollapsed,
      collapseTogglerIcon,
      toggleCollapsed,
      isMouseHovered,
      updateMouseHovered,
      handleMouseHovered,
      sidebarToggleIcon,
      menuSearch,
      isSidebarSearchFocused,
      sidebarSearchInput,
      hasSearch,
      handleMenuClick,
      unlockSidebarSearch,
      handleSidebarSearchBlur,
      displayName,
      roleName,
      userInitials,
      logout,

      // Shadow Bottom
      shallShadowBottom,

      // Skin
      skin,

      // App Name
      appName,
      appLogoImage,
    }
  },
}
</script>

<style lang="scss">
@import "~@core/scss/base/core/menu/menu-types/vertical-menu.scss";

.main-menu {
  position: relative;
  display: flex !important;
  flex-direction: column;

  .main-menu-content {
    flex: 1 1 auto;
    min-height: 0;
    height: auto !important;
  }

  .navbar-header .navbar-brand {
    align-items: center;
  }

  .brand-text {
    letter-spacing: 0;
    font-weight: 700;
  }

  .sidebar-collapse-btn {
    position: relative;
    box-sizing: border-box;
    width: 34px;
    height: 34px;
    padding: 0 !important;
    margin-top: 0.1rem;
    line-height: 1;
    color: #2b8a7e;
    border: 1px solid rgba(43, 138, 126, 0.18);
    border-radius: 50%;
    background:
      linear-gradient(180deg, rgba(255, 255, 255, 0.98), rgba(247, 250, 252, 0.94));
    box-shadow:
      inset 0 1px 0 rgba(255, 255, 255, 0.9),
      0 8px 18px rgba(32, 36, 54, 0.08);
    transition: color 0.18s ease, border-color 0.18s ease, background 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease;

    // NOTE: this must stay scoped to the mobile/tablet breakpoint (below xl).
    // The template already restricts this button to `d-block d-xl-none`
    // (Bootstrap's `d-xl-none` sets `display: none !important` at xl+), but
    // an earlier unconditional `display: inline-grid !important` here on the
    // base selector out-cascaded that utility class and kept the button
    // visible on desktop too. Scoping the grid/place-items display to the
    // same below-xl range keeps it in sync with the template's breakpoint.
    @media (max-width: 1199.98px) {
      display: inline-grid !important;
      place-items: center;
    }

    svg {
      display: block;
      margin: 0 auto;
    }

    &:hover {
      color: #fff;
      border-color: rgba(43, 138, 126, 0.72);
      background: linear-gradient(135deg, #2b8a7e, #255f85);
      box-shadow:
        0 10px 24px rgba(43, 138, 126, 0.24),
        0 3px 8px rgba(32, 36, 54, 0.08);
      transform: translateY(-1px);
    }

    &:active {
      transform: translateY(0);
      box-shadow: 0 5px 12px rgba(43, 138, 126, 0.18);
    }
  }

  &.menu-dark .sidebar-collapse-btn {
    color: #baf0e9;
    border-color: rgba(255, 255, 255, 0.12);
    background: rgba(255, 255, 255, 0.08);
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.08);

    &:hover {
      color: #fff;
      border-color: rgba(186, 240, 233, 0.34);
      background: linear-gradient(135deg, #2b8a7e, #255f85);
    }
  }

  // Circular collapse toggle anchored to the sidebar's edge, vertically centered
  .sidebar-edge-toggle {
    position: absolute;
    top: 50%;
    right: -14px;
    z-index: 20;
    display: grid;
    width: 28px;
    height: 28px;
    padding: 0;
    place-items: center;
    color: #2b8a7e;
    border: 1px solid rgba(43, 138, 126, 0.22);
    border-radius: 50%;
    background: #fff;
    box-shadow:
      0 4px 12px rgba(32, 36, 54, 0.14),
      inset 0 1px 0 rgba(255, 255, 255, 0.9);
    transform: translateY(-50%);
    transition: color 0.18s ease, border-color 0.18s ease, background 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease;

    .collapse-toggle-icon {
      stroke-width: 2.6;
      transition: transform 0.18s ease;
    }

    &:hover {
      color: #fff;
      border-color: transparent;
      background: linear-gradient(135deg, #2b8a7e, #255f85);
      box-shadow: 0 8px 20px rgba(43, 138, 126, 0.32);
      transform: translateY(-50%) scale(1.08);
    }

    &:active {
      transform: translateY(-50%) scale(0.96);
    }

    &:focus {
      outline: none;
    }
  }

  &.menu-dark .sidebar-edge-toggle {
    color: #baf0e9;
    border-color: rgba(255, 255, 255, 0.14);
    background: #161d31;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.32);

    &:hover {
      color: #fff;
      background: linear-gradient(135deg, #2b8a7e, #255f85);
    }
  }

  .sidebar-search {
    position: relative;
    z-index: 5;
    padding: 0.75rem 1rem 0.5rem;
    pointer-events: auto;
    transition: opacity 0.2s ease;

    .input-group {
      border: 1px solid rgba(34, 41, 47, 0.08);
      border-radius: 8px;
      overflow: hidden;
      background: #fff;
      box-shadow: 0 8px 18px rgba(34, 41, 47, 0.04);
      pointer-events: auto;
    }

    .input-group-text,
    .form-control {
      border: 0;
      background: transparent;
    }

    .form-control {
      min-height: 38px;
      font-size: 0.86rem;
      pointer-events: auto;
      user-select: text;
    }
  }

  &.menu-dark .sidebar-search {
    .input-group {
      border-color: rgba(255, 255, 255, 0.08);
      background: rgba(255, 255, 255, 0.06);
    }

    .input-group-text,
    .form-control {
      color: #d8d6de;
    }
  }

  &:not(.expanded):not(.sidebar-search-active) .sidebar-search {
    height: 0;
    padding: 0;
    opacity: 0;
    overflow: hidden;
  }

  &:not(.expanded) {
    .navigation-main {
      > .nav-item.has-sub {
        margin: 0.2rem 0.55rem;

        > a {
          justify-content: center;
          width: 44px;
          height: 44px;
          padding: 0 !important;
          border-radius: 8px;
        }

        .menu-title,
        .badge,
        .menu-content {
          display: none !important;
        }
      }

      > .nav-item:not(.has-sub) {
        margin: 0.2rem 0.55rem;

        > a {
          justify-content: center;
          width: 44px;
          height: 44px;
          padding: 0 !important;
          border-radius: 8px;

          .menu-title,
          .badge {
            display: none !important;
          }
        }
      }
    }
  }

  .sidebar-account-panel {
    position: relative;
    z-index: 4;
    flex: 0 0 auto;
    padding: 0.75rem 0.9rem 0.95rem;
    border-top: 1px solid rgba(34, 41, 47, 0.08);
    background:
      linear-gradient(180deg, rgba(255, 255, 255, 0.88), #fff),
      radial-gradient(circle at 20% 0%, rgba(43, 138, 126, 0.1), transparent 34%);
  }

  .sidebar-account-card {
    display: flex;
    align-items: center;
    gap: 0.65rem;
    padding: 0.55rem;
    color: #2f3545;
    border: 1px solid rgba(34, 41, 47, 0.08);
    border-radius: 10px;
    background: rgba(255, 255, 255, 0.88);
    box-shadow: 0 10px 22px rgba(34, 41, 47, 0.06);

    &:hover {
      color: #1d6f66;
      transform: translateY(-1px);
    }
  }

  .sidebar-account-avatar {
    flex: 0 0 36px;
    font-weight: 800;
  }

  .sidebar-account-card__copy {
    display: flex;
    min-width: 0;
    flex-direction: column;

    strong,
    small {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    strong {
      font-size: 0.86rem;
      line-height: 1.2;
    }

    small {
      margin-top: 0.12rem;
      color: #7b8190;
      font-size: 0.72rem;
      font-weight: 700;
    }
  }

  .sidebar-account-actions {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0.45rem;
    margin-top: 0.55rem;
  }

  .sidebar-account-action {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.35rem;
    min-height: 34px;
    padding: 0.45rem 0.55rem;
    color: #475064;
    border: 1px solid rgba(34, 41, 47, 0.08);
    border-radius: 9px;
    background: #fff;
    font-size: 0.78rem;
    font-weight: 800;
    line-height: 1;
    transition: all 0.18s ease;

    &:hover {
      color: #fff;
      border-color: rgba(43, 138, 126, 0.72);
      background: linear-gradient(135deg, #2b8a7e, #255f85);
      box-shadow: 0 10px 22px rgba(43, 138, 126, 0.2);
    }
  }

  .sidebar-account-action--danger {
    cursor: pointer;

    &:hover {
      border-color: rgba(234, 84, 85, 0.45);
      background: linear-gradient(135deg, #ea5455, #b93f55);
      box-shadow: 0 10px 22px rgba(234, 84, 85, 0.18);
    }
  }

  &.menu-dark .sidebar-account-panel {
    border-top-color: rgba(255, 255, 255, 0.08);
    background:
      linear-gradient(180deg, rgba(22, 29, 49, 0.9), #161d31),
      radial-gradient(circle at 20% 0%, rgba(43, 138, 126, 0.18), transparent 34%);
  }

  &.menu-dark .sidebar-account-card,
  &.menu-dark .sidebar-account-action {
    color: #d8d6de;
    border-color: rgba(255, 255, 255, 0.08);
    background: rgba(255, 255, 255, 0.06);
  }

  &.menu-dark .sidebar-account-card__copy small {
    color: rgba(216, 214, 222, 0.72);
  }

  .dark-layout & .sidebar-account-panel,
  body.dark-layout & .sidebar-account-panel {
    border-top-color: rgba(255, 255, 255, 0.08);
    background:
      linear-gradient(180deg, rgba(22, 29, 49, 0.94), #111827),
      radial-gradient(circle at 20% 0%, rgba(73, 184, 171, 0.18), transparent 34%);
  }

  .dark-layout & .sidebar-account-card,
  .dark-layout & .sidebar-account-action,
  body.dark-layout & .sidebar-account-card,
  body.dark-layout & .sidebar-account-action {
    color: #d8d6de;
    border-color: rgba(255, 255, 255, 0.08);
    background: rgba(255, 255, 255, 0.06);
  }

  .dark-layout & .sidebar-account-card__copy small,
  body.dark-layout & .sidebar-account-card__copy small {
    color: rgba(216, 214, 222, 0.72);
  }

  &:not(.expanded) .sidebar-account-panel {
    padding: 0.6rem 0.5rem 0.7rem;
  }

  &:not(.expanded) .sidebar-account-card {
    justify-content: center;
    padding: 0.35rem 0.25rem;
  }

  &:not(.expanded) .sidebar-account-card__copy {
    display: none;
  }

  &:not(.expanded) .sidebar-account-actions {
    display: grid;
    grid-template-columns: 1fr;
    gap: 0.35rem;
    margin-top: 0.45rem;
  }

  &:not(.expanded) .sidebar-account-action {
    min-height: 34px;
    padding: 0;

    span {
      display: none;
    }
  }

  .navigation-main {
    > .nav-item.has-sub {
      margin: 0.1rem 0.7rem;

      > a {
        border-radius: 8px;
      }
    }

    .menu-content .nav-item a {
      padding-left: 3.45rem;
    }
  }

  .sidebar-empty-state {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin: 1rem;
    padding: 0.75rem;
    color: #6e6b7b;
    border-radius: 8px;
    background: rgba(115, 103, 240, 0.08);
    list-style: none;
  }
}
</style>
